import { useState, useEffect, useRef } from 'react';
import { adminService } from '../services/api';
import logo from '../assets/logo.png';
import './AdminPanel.css';

const IconFile = () => (
  <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
  </svg>
);

const IconUsers = () => (
  <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const IconTrash = () => (
  <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6" />
    <path d="M14 11v6" />
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
);

const IconUpload = () => (
  <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

const IconSpreadsheet = () => (
  <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <line x1="3" y1="9" x2="21" y2="9" />
    <line x1="3" y1="15" x2="21" y2="15" />
    <line x1="9" y1="3" x2="9" y2="21" />
  </svg>
);

const IconDownload = () => (
  <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const IconKey = () => (
  <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
  </svg>
);

const IconPlus = () => (
  <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

function AdminPanel({ onLogout }) {
  const [tab, setTab] = useState('files');
  const [files, setFiles] = useState([]);
  const [conselheiros, setConselheiros] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [newAdminUser, setNewAdminUser] = useState('');
  const [newAdminPass, setNewAdminPass] = useState('');
  const [creatingAdmin, setCreatingAdmin] = useState(false);
  const fileInputRef = useRef(null);
  const xlsxInputRef = useRef(null);

  const currentAdminUser = (() => {
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) return '';
      return atob(token).split(':')[0] || '';
    } catch {
      return '';
    }
  })();

  useEffect(() => {
    if (tab === 'files') loadFiles();
    else if (tab === 'conselheiros') loadConselheiros();
    else if (tab === 'admins') loadAdmins();
  }, [tab]);

  const showMessage = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 4000);
  };

  const loadFiles = async () => {
    setLoading(true);
    try {
      const data = await adminService.listFiles();
      setFiles(data);
    } catch {
      showMessage('Erro ao carregar arquivos', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadConselheiros = async () => {
    setLoading(true);
    try {
      const data = await adminService.listConselheiros();
      setConselheiros(data);
    } catch {
      showMessage('Erro ao carregar conselheiros', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadAdmins = async () => {
    setLoading(true);
    try {
      const data = await adminService.listAdmins();
      setAdmins(data);
    } catch {
      showMessage('Erro ao carregar admins', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadConselheiros = async () => {
    setDownloading(true);
    try {
      const blob = await adminService.downloadConselheiros();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'Conselheiros Adimplentes - 270126.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      showMessage('Erro ao baixar planilha', 'error');
    } finally {
      setDownloading(false);
    }
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    const cleanUser = newAdminUser
      .replace(/[\u200B-\u200D\u2060\uFEFF]/g, '')
      .trim();
    if (!cleanUser || !newAdminPass) return;
    setCreatingAdmin(true);
    try {
      const result = await adminService.createAdmin(cleanUser, newAdminPass);
      if (result.success) {
        showMessage(result.message);
        setNewAdminUser('');
        setNewAdminPass('');
        loadAdmins();
      } else {
        showMessage(result.message || 'Erro ao criar admin', 'error');
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message || 'Erro ao criar admin';
      showMessage(Array.isArray(msg) ? msg.join(', ') : msg, 'error');
    } finally {
      setCreatingAdmin(false);
    }
  };

  const handleDeleteAdmin = async (username) => {
    if (!window.confirm(`Remover admin "${username}"?`)) return;
    setDeleting(username);
    try {
      const result = await adminService.deleteAdmin(username);
      if (result.success) {
        showMessage(result.message);
        loadAdmins();
      } else {
        showMessage(result.message || 'Erro ao remover', 'error');
      }
    } catch (err) {
      const msg = err?.response?.data?.message || 'Erro ao remover admin';
      showMessage(Array.isArray(msg) ? msg.join(', ') : msg, 'error');
    } finally {
      setDeleting(null);
    }
  };

  const handleFileUpload = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length === 0) return;

    setUploading(true);
    let successCount = 0;
    let failCount = 0;

    for (const file of selectedFiles) {
      try {
        const result = await adminService.uploadFile(file);
        if (result.success) {
          successCount++;
        } else {
          failCount++;
        }
      } catch {
        failCount++;
      }
    }

    if (failCount === 0) {
      showMessage(`${successCount} arquivo(s) enviado(s) com sucesso`);
    } else {
      showMessage(`${successCount} enviado(s), ${failCount} com erro`, failCount > 0 ? 'error' : 'success');
    }

    loadFiles();
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDeleteFile = async (filename) => {
    if (!window.confirm(`Tem certeza que deseja apagar "${filename}"?`)) return;

    setDeleting(filename);
    try {
      const result = await adminService.deleteFile(filename);
      if (result.success) {
        showMessage(`Arquivo "${filename}" removido`);
        loadFiles();
      } else {
        showMessage(result.message || 'Erro ao remover', 'error');
      }
    } catch {
      showMessage('Erro ao remover arquivo', 'error');
    } finally {
      setDeleting(null);
    }
  };

  const handleXlsxUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const result = await adminService.uploadConselheiros(file);
      if (result.success) {
        showMessage(result.message);
        loadConselheiros();
      } else {
        showMessage(result.message || 'Erro no upload', 'error');
      }
    } catch {
      showMessage('Erro ao enviar planilha', 'error');
    } finally {
      setUploading(false);
      if (xlsxInputRef.current) xlsxInputRef.current.value = '';
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    onLogout();
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatCPF = (cpf) => {
    return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  return (
    <div className="admin-container">
      <header className="admin-header">
        <div className="admin-header-content">
          <div className="admin-header-left">
            <img src={logo} alt="Fortaleza EC" className="admin-logo" />
            <div>
              <h1>Painel Admin</h1>
              <p>Gerenciamento do Portal</p>
            </div>
          </div>
          <button onClick={handleLogout} className="admin-logout-btn">
            Sair
          </button>
        </div>
      </header>

      {message && (
        <div className={`admin-message ${message.type}`}>
          {message.text}
        </div>
      )}

      <nav className="admin-tabs">
        <button
          className={`admin-tab ${tab === 'files' ? 'active' : ''}`}
          onClick={() => setTab('files')}
        >
          <IconFile /> Arquivos PDF
        </button>
        <button
          className={`admin-tab ${tab === 'conselheiros' ? 'active' : ''}`}
          onClick={() => setTab('conselheiros')}
        >
          <IconUsers /> Conselheiros
        </button>
        <button
          className={`admin-tab ${tab === 'admins' ? 'active' : ''}`}
          onClick={() => setTab('admins')}
        >
          <IconKey /> Admins
        </button>
      </nav>

      <main className="admin-main">
        {tab === 'files' && (
          <div className="admin-section">
            <div className="admin-section-header">
              <h2>Arquivos Disponíveis ({files.length})</h2>
              <div className="admin-upload-area">
                <input
                  type="file"
                  accept=".pdf"
                  multiple
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                />
                <button
                  className="admin-upload-btn"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                >
                  {uploading ? 'Enviando...' : <><IconUpload /> Upload PDF</>}
                </button>
              </div>
            </div>

            {loading ? (
              <div className="admin-loading">Carregando...</div>
            ) : files.length === 0 ? (
              <div className="admin-empty">Nenhum arquivo encontrado</div>
            ) : (
              <div className="admin-table-wrapper">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Nome do Arquivo</th>
                      <th>Tamanho</th>
                      <th>Modificado</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {files.map((file) => (
                      <tr key={file.name}>
                        <td className="file-name"><IconFile /> {file.name}</td>
                        <td>{formatSize(file.size)}</td>
                        <td>
                          {new Date(file.modified).toLocaleDateString('pt-BR')}
                        </td>
                        <td>
                          <button
                            className="admin-delete-btn"
                            onClick={() => handleDeleteFile(file.name)}
                            disabled={deleting === file.name}
                          >
                            {deleting === file.name ? '...' : <><IconTrash /> Apagar</>}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {tab === 'conselheiros' && (
          <div className="admin-section">
            <div className="admin-section-header">
              <h2>Conselheiros Adimplentes ({conselheiros.length})</h2>
              <div className="admin-upload-area">
                <input
                  type="file"
                  accept=".xlsx"
                  ref={xlsxInputRef}
                  onChange={handleXlsxUpload}
                  style={{ display: 'none' }}
                />
                <button
                  className="admin-upload-btn admin-secondary-btn"
                  onClick={handleDownloadConselheiros}
                  disabled={downloading}
                >
                  {downloading ? 'Baixando...' : <><IconDownload /> Baixar Planilha</>}
                </button>
                <button
                  className="admin-upload-btn"
                  onClick={() => xlsxInputRef.current?.click()}
                  disabled={uploading}
                >
                  {uploading ? 'Enviando...' : <><IconSpreadsheet /> Atualizar Planilha (.xlsx)</>}
                </button>
              </div>
            </div>

            {loading ? (
              <div className="admin-loading">Carregando...</div>
            ) : conselheiros.length === 0 ? (
              <div className="admin-empty">
                Nenhum conselheiro encontrado. Faça upload da planilha.
              </div>
            ) : (
              <div className="admin-table-wrapper">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Nome</th>
                      <th>CPF</th>
                      <th>Email</th>
                    </tr>
                  </thead>
                  <tbody>
                    {conselheiros.map((c, i) => (
                      <tr key={c.cpf}>
                        <td>{i + 1}</td>
                        <td>{c.nome}</td>
                        <td>{formatCPF(c.cpf)}</td>
                        <td>{c.email}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {tab === 'admins' && (
          <div className="admin-section">
            <div className="admin-section-header">
              <h2>Usuários Admin ({admins.length})</h2>
            </div>

            <form className="admin-create-form" onSubmit={handleCreateAdmin}>
              <div className="admin-create-fields">
                <input
                  type="text"
                  className="admin-input"
                  placeholder="Nome de usuário"
                  value={newAdminUser}
                  onChange={(e) => setNewAdminUser(e.target.value)}
                  autoComplete="off"
                  required
                  minLength={3}
                />
                <input
                  type="password"
                  className="admin-input"
                  placeholder="Senha (mínimo 6 caracteres)"
                  value={newAdminPass}
                  onChange={(e) => setNewAdminPass(e.target.value)}
                  autoComplete="new-password"
                  required
                  minLength={6}
                />
                <button
                  type="submit"
                  className="admin-upload-btn"
                  disabled={creatingAdmin}
                >
                  {creatingAdmin ? 'Criando...' : <><IconPlus /> Criar Admin</>}
                </button>
              </div>
            </form>

            {loading ? (
              <div className="admin-loading">Carregando...</div>
            ) : admins.length === 0 ? (
              <div className="admin-empty">Nenhum admin cadastrado</div>
            ) : (
              <div className="admin-table-wrapper">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Usuário</th>
                      <th>Criado em</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {admins.map((a) => {
                      const isSelf =
                        a.username.toLowerCase() ===
                        currentAdminUser.toLowerCase();
                      return (
                        <tr key={a.username}>
                          <td>
                            <IconKey /> {a.username}
                            {a.isDefault && (
                              <span className="admin-badge">padrão</span>
                            )}
                            {isSelf && (
                              <span className="admin-badge admin-badge-self">você</span>
                            )}
                          </td>
                          <td>
                            {a.createdAt
                              ? new Date(a.createdAt).toLocaleString('pt-BR')
                              : '—'}
                          </td>
                          <td>
                            <button
                              className="admin-delete-btn"
                              onClick={() => handleDeleteAdmin(a.username)}
                              disabled={
                                deleting === a.username || a.isDefault || isSelf
                              }
                              title={
                                a.isDefault
                                  ? 'Admin padrão não pode ser removido'
                                  : isSelf
                                  ? 'Não é possível remover o próprio usuário'
                                  : 'Remover admin'
                              }
                            >
                              {deleting === a.username ? '...' : <><IconTrash /> Remover</>}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

export default AdminPanel;
